import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import {
  TEMPLATE_PAGE_DASHBOARD,
  TEMPLATE_PAGE_ENERGY_COMMUNITY,
  TEMPLATE_PAGE_GLOBAL,
  TEMPLATING_TRANSLATION_NAMESPACE,
} from 'consts';
import { Context } from 'context/Context';
import {
  allItemTemplate,
  deepCopy,
  DEFAULT_DRAFT,
  defaultQueryProps, genericConf,
  TEMPLATE_PROPERTIES_INIT_STATE,
} from 'utils';

export const useTemplateV2 = false;

const BASE_VERSION = '0.1.3';
const UNKNOWN_VERSION = '0.0.0';

export const TEMPLATE_BASE_VERSIONS = {
  [TEMPLATE_PAGE_DASHBOARD]: BASE_VERSION,
  [TEMPLATE_PAGE_ENERGY_COMMUNITY]: BASE_VERSION,
};

export default function useTemplate(pagePath, lightMode = false) {
  const { t } = useTranslation(TEMPLATING_TRANSLATION_NAMESPACE);
  const initialConfState = deepCopy(genericConf(pagePath));
  const [currentTemplate, setCurrentTemplate] = useState(initialConfState);
  const [templateList, setTemplateList] = useState([]);
  const [isShowFavTempModal, setIsShowFavTempModal] = useState(false);
  const [customTemplateInput, setCustomTemplateInput] = useState({});
  const [templateProperties, setTemplateProperties] = useState(TEMPLATE_PROPERTIES_INIT_STATE);

  const modalType = (
    templateProperties.id === 'draftToAdd'
    && (!customTemplateInput.id || customTemplateInput.id === 'draftToAdd')
  ) ? 'save' : 'edit';

  const customTemplateGotKeys = Object.keys(customTemplateInput)?.length !== 0;

  const {
    api, accessToken, tenant, notifyManager,
  } = useContext(Context);

  const baseLiteral = 'template';
  const methods = api[useTemplateV2 ? `${baseLiteral}V2` : baseLiteral];

  const goToAll = () => {
    const toRet = initialConfState;
    setCurrentTemplate(toRet);
    setTemplateProperties(TEMPLATE_PROPERTIES_INIT_STATE);
    // no, non si può togliere
    // eslint-disable-next-line no-use-before-define
    updateTemplateList(-1);
    return toRet;
  };

  const getValidTemplate = (current) => {
    if (!current || !current.templateContent) return null;

    const {
      id,
      isActive,
      isFavorite,
      name,
      page,
      pageReference,
      version,
    } = current;

    const refToCheck = page || pageReference;

    if (!TEMPLATE_BASE_VERSIONS[refToCheck]) return goToAll();

    try {
      const templateContent = JSON.parse(current.templateContent);
      const versToCheck = version || templateContent.version;

      if (versToCheck !== TEMPLATE_BASE_VERSIONS[refToCheck]) {
        notifyManager.notifyWarn(t('obsoleteTemplate', { templateName: name }));
        return goToAll();
      }
      setCurrentTemplate(templateContent);
      setTemplateProperties({
        id,
        isActive,
        isFavorite,
        label: name,
        version,
      });
      return current;
    } catch {
      return goToAll();
    }
  };

  const {
    isFetching: isGetPageTemplatesFetching,
    data: getPageTemplatesData,
    refetch: triggerRefetch,
  } = useQuery(
    [`getAppPageTemplate@${pagePath}`, pagePath],
    () => methods.getAppPageTemplate(useTemplateV2 ? { page: pagePath } : pagePath, (err, showError) => {
      goToAll(); if (err.exceptionCause.code !== 9006) showError(); // 9906 TBR when template v2 will be adopted
    }),
    {
      ...defaultQueryProps,
      cacheTime: 0,
      keepPreviousData: true,
      enabled: !!accessToken && !!pagePath && !lightMode && pagePath !== TEMPLATE_PAGE_GLOBAL,
    },
  );

  const getTemplateById = async (templateId) => {
    const template = await methods.getTemplateById(templateId);
    return getValidTemplate(template);
  };

  const tListNormalizer = (activeTemplateId, callback) => {
    const templateNormalizedWithAllLabel = getPageTemplatesData?.length
      ? [...getPageTemplatesData, allItemTemplate] : [allItemTemplate];

    const templateNormalized = templateNormalizedWithAllLabel.map((curr) => ({
      callback,
      favorite: curr.isFavorite,
      id: curr.id,
      label: curr.name,
      page: curr.page || curr.pageReference,
      selected: curr.id === activeTemplateId,
      version: curr.version,
    }));

    setTemplateList(templateNormalized);
  };

  const selectTemplate = (newTemplate) => {
    setCustomTemplateInput({});
    if (newTemplate.label === 'All') {
      goToAll();
    } else if (newTemplate.id === 'draftToAdd') {
      setTemplateProperties({
        ...templateProperties,
        ...DEFAULT_DRAFT(),
      });
    } else {
      const idOfTemplateClicked = getPageTemplatesData?.length > 0 ? getPageTemplatesData
        .filter((val) => newTemplate.label === val.name)[0]?.id ?? undefined
        : undefined;
      if (idOfTemplateClicked) {
        getTemplateById(idOfTemplateClicked);
        tListNormalizer(idOfTemplateClicked, selectTemplate);
      }
    }
  };

  const updateTemplateList = (
    activeTemplateId,
  ) => {
    tListNormalizer(activeTemplateId, selectTemplate);
  };

  // get active templates to display active on tab bar
  const {
    isFetching: isGetActiveTemplateFetching,
    data: getActiveTemplateData,
  } = useQuery(
    [`getAppActiveTemplates@${pagePath}`, getPageTemplatesData],
    async () => {
      const data = useTemplateV2
        ? getPageTemplatesData.find((template) => template.isActive === true) : await api.template.getActiveTemplate(pagePath);

      const aT = getValidTemplate(data);
      if (!aT) return goToAll();
      updateTemplateList(aT.id);
      return aT;
    },
    {
      ...defaultQueryProps, keepPreviousData: true, cacheTime: 0, enabled: !!accessToken && !!getPageTemplatesData,
    },
  );

  // get fav templates for starMenu
  const { data: favouriteTemplateData, isFetching: isFavouriteTemplateLoading, refetch: refetchFavTemplates } = useQuery(
    'getAppFavoriteTemplates',
    async () => {
      let data = [];
      if (useTemplateV2) {
        data = await api.templateV2.getAppPageTemplate({
          isFavorite: true,
        });
      } else {
        data = await api.template.getAppFavoriteTemplates();
      }

      return data;
    },
    { ...defaultQueryProps, enabled: !!accessToken },
  );

  // function for edit template
  const editCurrentTemplate = async (templateData) => {
    if (templateProperties.label === 'All' && !customTemplateGotKeys) return;

    const {
      isActive,
      isFavorite,
      label,
      labelToCheck,
    } = templateData ?? {};

    let getRealTeamplateContent = {};

    const isCurrTempEditing = labelToCheck === templateProperties.label;

    const templateProps = {
      id: customTemplateGotKeys ? customTemplateInput.id : templateProperties.id,
      isActive,
      isFavorite,
      label,
      version: customTemplateGotKeys ? customTemplateInput.version : templateProperties.version,
    };

    if (!isCurrTempEditing) {
      getRealTeamplateContent = await methods.getTemplateById(templateProps.id);
      if (Object.keys(getRealTeamplateContent).length === 0) {
        notifyManager.notifyError(t('genericErrors.templateId'));
        return;
      }
    }

    const postTemplateContent = isCurrTempEditing
      ? currentTemplate : JSON.parse(getRealTeamplateContent.templateContent);

    const updateResult = await methods.updateAppPageTemplate(
      pagePath,
      { ...templateProps, templateContent: JSON.stringify(postTemplateContent) },
    );

    if (updateResult) {
      triggerRefetch();
      refetchFavTemplates();
      notifyManager.notifySuccess(t('successEdit'));
      setTemplateProperties(templateProps);

      setTemplateList(templateList.map((currentTheme) => (
        currentTheme.id === templateData.id ? {
          ...currentTheme,
          label: templateData.label,
          isFavorite: templateData.isFavorite,
          isActive: templateData.isActive,
        } : currentTheme
      )));
      setCustomTemplateInput({});
    }
  };

  // delete template
  const deleteCurrentTemplate = async () => {
    if (templateProperties.label === 'All' && !customTemplateGotKeys) return;
    const deleteResult = await methods.deleteTemplateById(
      customTemplateGotKeys ? customTemplateInput.id : templateProperties.id,
    );
    if (deleteResult) {
      notifyManager.notifySuccess(t('successDelete'));
      setCustomTemplateInput({});
      triggerRefetch();
      refetchFavTemplates();
    }
  };

  // save current template draft
  const saveCurrentTemplate = async (templateToSave) => {
    if (templateToSave.label === 'All' && !customTemplateGotKeys) return;

    const templateProps = {
      label: templateToSave.label,
      isActive: templateToSave.isActive,
      isFavorite: templateToSave.isFavorite,
    };

    const version = TEMPLATE_BASE_VERSIONS[pagePath] ?? UNKNOWN_VERSION;

    const createResult = await methods.createAppPageTemplate(
      pagePath,
      {
        ...templateProps,
        version,
        templateContent: JSON.stringify({
          ...currentTemplate,
          version,
        }),
      },
    );

    if (createResult) {
      setTemplateList(templateList.map((currentTheme) => (
        currentTheme.id === templateToSave.id ? {
          ...currentTheme,
          ...templateProps,
        } : currentTheme
      )));
      triggerRefetch();
      refetchFavTemplates();
      notifyManager.notifySuccess(t('successSave'));
      setCustomTemplateInput({});
    }
  };

  const createTemplateDraft = () => {
    if (templateList.some((template) => template.id === 'draftToAdd')) {
      notifyManager.notifyError(t('errorDraftAlreadyExists'));
      return;
    }

    const draftToAdd = {
      ...DEFAULT_DRAFT(),
      callback: selectTemplate,
    };

    // eslint-disable-next-line no-param-reassign
    templateList.forEach((item) => { item.selected = false; });

    setTemplateList([...templateList, draftToAdd]);
    setTemplateProperties({
      id: draftToAdd.id,
      label: draftToAdd.label,
      isFavorite: draftToAdd.favorite,
      isActive: draftToAdd.default,
    });
  };

  const modifyAnotherTemplate = async (wheelTemplateClicked) => {
    const isTempDef = wheelTemplateClicked?.id === getActiveTemplateData?.id && getActiveTemplateData?.isActive;

    const templateToSet = {
      id: wheelTemplateClicked?.id,
      isActive: isTempDef,
      isFavorite: wheelTemplateClicked?.favorite,
      label: wheelTemplateClicked?.label,
      version: wheelTemplateClicked?.version,
    };

    if (wheelTemplateClicked?.id !== 'draftToAdd') {
      const templateClickedFromWheel = await methods.getTemplateById(wheelTemplateClicked?.id);
      const templateFromWheelContent = JSON.parse(templateClickedFromWheel?.templateContent);

      const versToCheck = wheelTemplateClicked.version || templateFromWheelContent.version;

      if (versToCheck !== TEMPLATE_BASE_VERSIONS[wheelTemplateClicked.page]) {
        setCustomTemplateInput({
          ...templateToSet,
          editDisabled: true,
          version: versToCheck,
        });
        setIsShowFavTempModal(true);

        return;
      }
    }

    setCustomTemplateInput(templateToSet);

    setIsShowFavTempModal(true);
  };

  return {
    state: {
      get: {
        currentTemplate,
        templateProperties,
        templateList,
        modalType,
        isShowFavTempModal,
        customTemplateInput,
        initialConfState,
      },
      set: {
        setCurrentTemplate,
        setIsShowFavTempModal,
      },
    },
    data: {
      appPageTemplates: {
        isGetPageTemplatesFetching,
      },
      appActiveTemplates: {
        isGetActiveTemplateFetching,
        getActiveTemplateData,
      },
      appFavoriteTemplates: {
        favouriteTemplateData,
        isFavouriteTemplateLoading,
      },
      templateConditionToFetch: !!tenant && !!accessToken && !isGetPageTemplatesFetching && !isGetActiveTemplateFetching,
    },
    func: {
      createTemplateDraft,
      deleteCurrentTemplate,
      editCurrentTemplate,
      getTemplateById,
      modifyAnotherTemplate,
      saveCurrentTemplate,
      updateTemplateList,
    },
  };
}
